import CarrierDropdown from '../../../../components/filter-editor/components/carrier-dropdown';
import StoreDropdown from '../../../../components/filter-editor/components/store-dropdown';
import TagsDropdown from '../../../../components/filter-editor/components/tags-dropdown';
import ShipFromDropdown from '../../../../components/filter-editor/components/ship-from-dropdown';
import TextInput from '../../../../components/filter-editor/components/text-input';
import WeightInput from '../../../../components/filter-editor/components/weight-input';
import DatePaid from '../../../../components/filter-editor/components/date-paid';
import NoInputComponent from '../../../../components/filter-editor/components/no-input-component';
import MarketplaceDropdown from '../../../../components/filter-editor/components/marketplace-dropdown';
import CountryDropdown from '../../../../components/filter-editor/components/country-dropdown';
import StateDropdown from '../../../../components/filter-editor/components/state-dropdown';
import AssignedUserDropdown from '../../../../components/filter-editor/components/assigned-user-dropdown';
import PackageDropdown from '../../../../components/filter-editor/components/package-dropdown';
import { ShippingAccountSearchableDropdown } from '../../../../components/filter-editor/components/shipping-account-dropdown';
import SellerBrandDropdown from '../../../../components/filter-editor/components/seller-brand-dropdown';
import { FulfillmentProviderDropdown } from '../../../../components/filter-editor/components/fulfillment-provider-dropdown';
import ResidentialIndicatorComponent from '../../../../components/filter-editor/components/residential-indicator';
import AddressVerificationDropdown from '../../../../components/filter-editor/components/address-verification-dropdown';
import RequestedServiceMapped from '../../../../components/filter-editor/components/requested-service-mapped';
import { OrderImportTimeInput } from '../../../../components/filter-editor/components/order-import-time-input';

import {
  OPERATOR_TYPE_IS_BLANK,
  OPERATOR_TYPE_IS_NOT_BLANK,
  OPERATOR_TYPE_EQUALS,
  OPERATOR_TYPE_NOT_EQUALS,
  OPERATOR_TYPE_STARTS_WITH,
  OPERATOR_TYPE_DOES_NOT_START_WITH,
  OPERATOR_TYPE_ENDS_WITH,
  OPERATOR_TYPE_DOES_NOT_END_WITH,
  OPERATOR_TYPE_CONTAINS,
  OPERATOR_TYPE_DOES_NOT_CONTAIN,
  OPERATOR_TYPE_INCLUDES,
  OPERATOR_TYPE_DOES_NOT_INCLUDE,
  OPERATOR_TYPE_VALUE_ARE_EMPTY,
  OPERATOR_TYPE_VALUE_ARE_NOT_EMPTY,
  OPERATOR_TYPE_IS_UNKNOWN,
  OPERATOR_TYPE_SHORT_EQUALS,
  OPERATOR_TYPE_SHORT_NOT_EQUALS,
  OPERATOR_TYPE_GREATER_THAN,
  OPERATOR_TYPE_GREATER_THAN_OR_EQUAL_TO,
  OPERATOR_TYPE_LESS_THAN,
  OPERATOR_TYPE_LESS_THAN_OR_EQUAL_TO,
  OPERATOR_TYPE_CURRENT_DATE,
  OPERATOR_TYPE_WITHIN_PAST_X_DAYS,
  OPERATOR_TYPE_WITHIN_NEXT_X_DAYS,
  OPERATOR_TYPE_BEFORE_X_DAYS,
  OPERATOR_TYPE_AFTER_X_DAYS,
  OPERATOR_TYPE_ON_OR_AFTER,
  OPERATOR_TYPE_ON_OR_BEFORE,
  OPERATOR_TYPE_VALUE_EQUALS_TRUE,
  OPERATOR_TYPE_VALUE_EQUALS_FALSE,
  OPERATOR_TYPE_SHIPPING_MY_ACCOUNT,
  OPERATOR_TYPE_SHIPPING_RECIPIENT_ACCOUNT,
  OPERATOR_TYPE_SHIPPING_THIRD_PARTY_ACCOUNT,
  OPERATOR_TYPE_TIME_AT_OR_AFTER,
  OPERATOR_TYPE_TIME_AT_OR_BEFORE,
} from '../../../../components/filter-editor/constants/filter-operator-type';

import { filterMessages } from './messages';
import type { FilterRegistry } from './registry-types';
import { WORKFLOW_FILTER_TYPE } from '../../api/automation-workflow-types';

// Shared operator sets (mirrors options-for-filter-editor-type.js)
const TEXT_OPERATORS = [
  OPERATOR_TYPE_IS_BLANK,
  OPERATOR_TYPE_IS_NOT_BLANK,
  OPERATOR_TYPE_EQUALS,
  OPERATOR_TYPE_NOT_EQUALS,
  OPERATOR_TYPE_STARTS_WITH,
  OPERATOR_TYPE_DOES_NOT_START_WITH,
  OPERATOR_TYPE_ENDS_WITH,
  OPERATOR_TYPE_DOES_NOT_END_WITH,
  OPERATOR_TYPE_CONTAINS,
  OPERATOR_TYPE_DOES_NOT_CONTAIN,
] as const;

const NUMERIC_OPERATORS = [
  OPERATOR_TYPE_SHORT_EQUALS,
  OPERATOR_TYPE_SHORT_NOT_EQUALS,
  OPERATOR_TYPE_GREATER_THAN,
  OPERATOR_TYPE_GREATER_THAN_OR_EQUAL_TO,
  OPERATOR_TYPE_LESS_THAN,
  OPERATOR_TYPE_LESS_THAN_OR_EQUAL_TO,
] as const;

const DATE_OPERATORS = [
  OPERATOR_TYPE_CURRENT_DATE,
  OPERATOR_TYPE_WITHIN_PAST_X_DAYS,
  OPERATOR_TYPE_WITHIN_NEXT_X_DAYS,
  OPERATOR_TYPE_BEFORE_X_DAYS,
  OPERATOR_TYPE_AFTER_X_DAYS,
  OPERATOR_TYPE_ON_OR_AFTER,
  OPERATOR_TYPE_ON_OR_BEFORE,
] as const;

// Shared value input maps (mirrors value-input-from-editor-type-and-operator.js)
const TEXT_VALUE_INPUTS = {
  [OPERATOR_TYPE_IS_BLANK]: NoInputComponent,
  [OPERATOR_TYPE_IS_NOT_BLANK]: NoInputComponent,
  [OPERATOR_TYPE_EQUALS]: TextInput,
  [OPERATOR_TYPE_NOT_EQUALS]: TextInput,
  [OPERATOR_TYPE_STARTS_WITH]: TextInput,
  [OPERATOR_TYPE_DOES_NOT_START_WITH]: TextInput,
  [OPERATOR_TYPE_ENDS_WITH]: TextInput,
  [OPERATOR_TYPE_DOES_NOT_END_WITH]: TextInput,
  [OPERATOR_TYPE_CONTAINS]: TextInput,
  [OPERATOR_TYPE_DOES_NOT_CONTAIN]: TextInput,
};

const NUMERIC_VALUE_INPUTS = {
  [OPERATOR_TYPE_SHORT_EQUALS]: TextInput,
  [OPERATOR_TYPE_SHORT_NOT_EQUALS]: TextInput,
  [OPERATOR_TYPE_GREATER_THAN]: TextInput,
  [OPERATOR_TYPE_GREATER_THAN_OR_EQUAL_TO]: TextInput,
  [OPERATOR_TYPE_LESS_THAN]: TextInput,
  [OPERATOR_TYPE_LESS_THAN_OR_EQUAL_TO]: TextInput,
};

const WEIGHT_VALUE_INPUTS = {
  [OPERATOR_TYPE_SHORT_EQUALS]: WeightInput,
  [OPERATOR_TYPE_SHORT_NOT_EQUALS]: WeightInput,
  [OPERATOR_TYPE_GREATER_THAN]: WeightInput,
  [OPERATOR_TYPE_GREATER_THAN_OR_EQUAL_TO]: WeightInput,
  [OPERATOR_TYPE_LESS_THAN]: WeightInput,
  [OPERATOR_TYPE_LESS_THAN_OR_EQUAL_TO]: WeightInput,
};

const DATE_VALUE_INPUTS = {
  [OPERATOR_TYPE_CURRENT_DATE]: NoInputComponent,
  [OPERATOR_TYPE_WITHIN_PAST_X_DAYS]: TextInput,
  [OPERATOR_TYPE_WITHIN_NEXT_X_DAYS]: TextInput,
  [OPERATOR_TYPE_BEFORE_X_DAYS]: TextInput,
  [OPERATOR_TYPE_AFTER_X_DAYS]: TextInput,
  [OPERATOR_TYPE_ON_OR_AFTER]: DatePaid,
  [OPERATOR_TYPE_ON_OR_BEFORE]: DatePaid,
};

// Order matches AR filter criteria order (editor-options.js)
export const OrderFilterRegistry: FilterRegistry = {
  ShipToVerificationStatus: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.addressVerification,
    operators: [OPERATOR_TYPE_INCLUDES, OPERATOR_TYPE_IS_UNKNOWN] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: AddressVerificationDropdown,
      [OPERATOR_TYPE_IS_UNKNOWN]: NoInputComponent,
    },
  },
  AddressLine1: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.addressLine1,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  AddressLine2: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.addressLine2,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  AmountPaid: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'numeric',
    labelMessage: filterMessages.amountPaid,
    operators: NUMERIC_OPERATORS,
    valueInputsByOperator: NUMERIC_VALUE_INPUTS,
  },
  AssignedTo: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.assignedUser,
    operators: [OPERATOR_TYPE_INCLUDES] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: AssignedUserDropdown,
    },
  },
  SellerBrandId: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.sellerBrand,
    operators: [
      OPERATOR_TYPE_INCLUDES,
      OPERATOR_TYPE_DOES_NOT_INCLUDE,
      OPERATOR_TYPE_VALUE_ARE_EMPTY,
      OPERATOR_TYPE_VALUE_ARE_NOT_EMPTY,
    ] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: SellerBrandDropdown,
      [OPERATOR_TYPE_DOES_NOT_INCLUDE]: SellerBrandDropdown,
      [OPERATOR_TYPE_VALUE_ARE_EMPTY]: NoInputComponent,
      [OPERATOR_TYPE_VALUE_ARE_NOT_EMPTY]: NoInputComponent,
    },
  },
  BuyerId: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.buyerId,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  BuyerName: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.buyerName,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  // Backend enum: CollectionFilterField.CarrierId
  CarrierId: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.carrier,
    operators: [OPERATOR_TYPE_INCLUDES, OPERATOR_TYPE_VALUE_ARE_EMPTY, OPERATOR_TYPE_VALUE_ARE_NOT_EMPTY] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: CarrierDropdown,
      [OPERATOR_TYPE_VALUE_ARE_EMPTY]: NoInputComponent,
      [OPERATOR_TYPE_VALUE_ARE_NOT_EMPTY]: NoInputComponent,
    },
  },
  City: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.city,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  CustomField1: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.customField1,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  CustomField2: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.customField2,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  CustomField3: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.customField3,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  ShipToCountryCode: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.country,
    operators: [OPERATOR_TYPE_INCLUDES, OPERATOR_TYPE_DOES_NOT_INCLUDE] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: CountryDropdown,
      [OPERATOR_TYPE_DOES_NOT_INCLUDE]: CountryDropdown,
    },
  },
  PaidDateTime: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'datetime',
    labelMessage: filterMessages.datePaid,
    operators: DATE_OPERATORS,
    valueInputsByOperator: DATE_VALUE_INPUTS,
  },
  DeliverByDateTime: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'datetime',
    labelMessage: filterMessages.deliverByDate,
    operators: DATE_OPERATORS,
    valueInputsByOperator: DATE_VALUE_INPUTS,
  },
  BillToParty: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.billToParty,
    operators: [
      OPERATOR_TYPE_SHIPPING_MY_ACCOUNT,
      OPERATOR_TYPE_SHIPPING_RECIPIENT_ACCOUNT,
      OPERATOR_TYPE_SHIPPING_THIRD_PARTY_ACCOUNT,
    ] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_SHIPPING_MY_ACCOUNT]: ShippingAccountSearchableDropdown,
      [OPERATOR_TYPE_SHIPPING_RECIPIENT_ACCOUNT]: NoInputComponent,
      [OPERATOR_TYPE_SHIPPING_THIRD_PARTY_ACCOUNT]: NoInputComponent,
    },
  },
  IsGift: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'boolean',
    labelMessage: filterMessages.isGift,
    operators: [OPERATOR_TYPE_VALUE_EQUALS_TRUE, OPERATOR_TYPE_VALUE_EQUALS_FALSE] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_VALUE_EQUALS_TRUE]: NoInputComponent,
      [OPERATOR_TYPE_VALUE_EQUALS_FALSE]: NoInputComponent,
    },
  },
  IsInternational: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'boolean',
    labelMessage: filterMessages.isInternational,
    operators: [OPERATOR_TYPE_VALUE_EQUALS_TRUE, OPERATOR_TYPE_VALUE_EQUALS_FALSE] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_VALUE_EQUALS_TRUE]: NoInputComponent,
      [OPERATOR_TYPE_VALUE_EQUALS_FALSE]: NoInputComponent,
    },
  },
  InternalNotes: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.internalNotes,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  ItemName: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.itemName,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  ItemSku: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.itemSku,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  LineItemCount: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'numeric',
    labelMessage: filterMessages.lineItemCount,
    operators: NUMERIC_OPERATORS,
    valueInputsByOperator: NUMERIC_VALUE_INPUTS,
  },
  // Backend enum: CollectionFilterField.MarketplaceId
  MarketplaceId: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.marketplace,
    operators: [OPERATOR_TYPE_INCLUDES] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: MarketplaceDropdown,
    },
  },
  NotesToBuyer: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.notesToBuyer,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  NotesFromBuyer: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.notesFromBuyer,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  OrderDateTime: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'datetime',
    labelMessage: filterMessages.orderDate,
    operators: DATE_OPERATORS,
    valueInputsByOperator: DATE_VALUE_INPUTS,
  },
  OrderNumber: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.orderNumber,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  OrderImportTime: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.orderImportTime,
    operators: [OPERATOR_TYPE_TIME_AT_OR_AFTER, OPERATOR_TYPE_TIME_AT_OR_BEFORE] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_TIME_AT_OR_AFTER]: OrderImportTimeInput,
      [OPERATOR_TYPE_TIME_AT_OR_BEFORE]: OrderImportTimeInput,
    },
  },
  Source: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.orderSource,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  OrderSubtotal: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'numeric',
    labelMessage: filterMessages.orderSubtotal,
    operators: NUMERIC_OPERATORS,
    valueInputsByOperator: NUMERIC_VALUE_INPUTS,
  },
  Tag: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.tags,
    operators: [OPERATOR_TYPE_INCLUDES, OPERATOR_TYPE_DOES_NOT_INCLUDE, OPERATOR_TYPE_VALUE_ARE_EMPTY] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: TagsDropdown,
      [OPERATOR_TYPE_DOES_NOT_INCLUDE]: TagsDropdown,
      [OPERATOR_TYPE_VALUE_ARE_EMPTY]: NoInputComponent,
    },
  },
  OrderTotal: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'numeric',
    labelMessage: filterMessages.orderTotal,
    operators: NUMERIC_OPERATORS,
    valueInputsByOperator: NUMERIC_VALUE_INPUTS,
  },
  OrderWeight: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'numeric',
    labelMessage: filterMessages.orderWeight,
    operators: NUMERIC_OPERATORS,
    valueInputsByOperator: WEIGHT_VALUE_INPUTS,
  },
  PackageTypeId: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.packageType,
    operators: [OPERATOR_TYPE_INCLUDES] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: PackageDropdown,
    },
  },
  PaymentMethod: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.paymentMethod,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  PostalCode: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.postalCode,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  ShipToCompany: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.recipientCompany,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  ShipToName: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.recipientName,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  RequestedServiceMapped: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.requestedServiceMapped,
    operators: [OPERATOR_TYPE_INCLUDES, OPERATOR_TYPE_DOES_NOT_INCLUDE, OPERATOR_TYPE_VALUE_ARE_EMPTY] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: RequestedServiceMapped,
      [OPERATOR_TYPE_DOES_NOT_INCLUDE]: RequestedServiceMapped,
      [OPERATOR_TYPE_VALUE_ARE_EMPTY]: NoInputComponent,
    },
  },
  RequestedServiceMarketplace: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.requestedServiceUnmapped,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  ResidentialIndicator: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.residentialIndicator,
    operators: [OPERATOR_TYPE_INCLUDES] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: ResidentialIndicatorComponent,
    },
  },
  SellerProviderId: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.shippingAccount,
    operators: [
      OPERATOR_TYPE_SHIPPING_MY_ACCOUNT,
      OPERATOR_TYPE_SHIPPING_RECIPIENT_ACCOUNT,
      OPERATOR_TYPE_SHIPPING_THIRD_PARTY_ACCOUNT,
    ] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_SHIPPING_MY_ACCOUNT]: ShippingAccountSearchableDropdown,
      [OPERATOR_TYPE_SHIPPING_RECIPIENT_ACCOUNT]: NoInputComponent,
      [OPERATOR_TYPE_SHIPPING_THIRD_PARTY_ACCOUNT]: NoInputComponent,
    },
  },
  ShippingPaid: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'numeric',
    labelMessage: filterMessages.shippingPaid,
    operators: NUMERIC_OPERATORS,
    valueInputsByOperator: NUMERIC_VALUE_INPUTS,
  },
  ShipByDateTime: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'datetime',
    labelMessage: filterMessages.shipByDate,
    operators: DATE_OPERATORS,
    valueInputsByOperator: DATE_VALUE_INPUTS,
  },
  ShipFromId: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.shipFrom,
    operators: [OPERATOR_TYPE_INCLUDES] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: ShipFromDropdown,
    },
  },
  ShopifyShipFromLocation: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.shopifyShipFrom,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  ShipToState: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.stateProvince,
    operators: [OPERATOR_TYPE_INCLUDES, OPERATOR_TYPE_DOES_NOT_INCLUDE] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: StateDropdown,
      [OPERATOR_TYPE_DOES_NOT_INCLUDE]: StateDropdown,
    },
  },
  StoreId: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.store,
    operators: [OPERATOR_TYPE_INCLUDES] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: StoreDropdown,
    },
  },
  TaxPaid: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'numeric',
    labelMessage: filterMessages.taxPaid,
    operators: NUMERIC_OPERATORS,
    valueInputsByOperator: NUMERIC_VALUE_INPUTS,
  },
  Quantity: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'numeric',
    labelMessage: filterMessages.totalQuantity,
    operators: NUMERIC_OPERATORS,
    valueInputsByOperator: NUMERIC_VALUE_INPUTS,
  },
  Zone: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'numeric',
    labelMessage: filterMessages.uspsZone,
    operators: NUMERIC_OPERATORS,
    valueInputsByOperator: NUMERIC_VALUE_INPUTS,
  },
  WarehouseLocation: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'string',
    labelMessage: filterMessages.warehouseLocation,
    operators: TEXT_OPERATORS,
    valueInputsByOperator: TEXT_VALUE_INPUTS,
  },
  SellerFillProviderId: {
    filterType: WORKFLOW_FILTER_TYPE.filterCriteria,
    filterKind: 'collection',
    labelMessage: filterMessages.fulfillmentProvider,
    operators: [
      OPERATOR_TYPE_INCLUDES,
      OPERATOR_TYPE_DOES_NOT_INCLUDE,
      OPERATOR_TYPE_VALUE_ARE_EMPTY,
      OPERATOR_TYPE_VALUE_ARE_NOT_EMPTY,
    ] as const,
    valueInputsByOperator: {
      [OPERATOR_TYPE_INCLUDES]: FulfillmentProviderDropdown,
      [OPERATOR_TYPE_DOES_NOT_INCLUDE]: FulfillmentProviderDropdown,
      [OPERATOR_TYPE_VALUE_ARE_EMPTY]: NoInputComponent,
      [OPERATOR_TYPE_VALUE_ARE_NOT_EMPTY]: NoInputComponent,
    },
  },
};
